import React, { useState } from "react";
import {
  calculateBankingCost,
  exportBankingExcel,
} from "../../../../Rest_api/RestApi";
import {
  BankingCalculateResponse,
  BankingPeriodResult,
} from "../../../../models/banking";
import { Plus, Trash2, Copy, Download, Zap, ArrowRight, ArrowLeft, ChevronDown, Settings2, Scale, Info } from "lucide-react";
import { PrimaryColor, SecondaryColor } from "./PriceAnalysisForm";
import { DynamicDatePicker } from "../../../../components/DatePicker";

// ── Row Interfaces ──
interface SupplyRow {
  id: string;
  startDate: string;
  endDate: string;
  mw: string;
  startTime: string;
  endTime: string;
}

interface ReturnRow {
  id: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  mw: string;
}

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

const generateTimeOptions = (): string[] => {
  const opts: string[] = [];
  for (let h = 0; h < 24; h++)
    for (let m = 0; m < 60; m += 15)
      opts.push(`${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`);
  opts.push("24:00");
  return opts;
};
const TIME_OPTIONS = generateTimeOptions();

function parseHours(st: string, et: string): number {
  const [sh, sm] = st.split(":").map(Number);
  const [eh, em] = et === "24:00" ? [24, 0] : et.split(":").map(Number);
  return (eh * 60 + em - sh * 60 - sm) / 60;
}

function formatCr(v: number): string {
  const cr = Math.abs(v) / 1e7;
  const s = v < 0 ? "-" : "";
  return cr >= 1 ? `${s}${cr.toFixed(2)} Cr` : `${s}${(Math.abs(v) / 1e5).toFixed(2)} L`;
}

function fmtN(v: number): string {
  return v.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function getPU(cost: number, mwh: number): string {
  return mwh > 0 ? (cost / (mwh * 1000)).toFixed(2) : "0.00";
}

// ── Custom Table ──
const TH: React.FC<{ children: React.ReactNode; align?: "left" | "center" | "right" }> = ({ children, align = "left" }) => (
  <div
    className="px-3 py-2 text-[10px] font-medium text-gray-400 uppercase tracking-wider"
    style={{ textAlign: align }}
  >
    {children}
  </div>
);

const TD: React.FC<{ children: React.ReactNode; align?: "left" | "center" | "right"; color?: string }> = ({
  children,
  align = "left",
  color,
}) => (
  <div
    className="px-3 py-2 text-xs text-gray-600"
    style={{ textAlign: align, color }}
  >
    {children}
  </div>
);

// ── Component ──
const formatLocal = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const BankingCalculatorForm: React.FC = () => {
  const [supplyRows, setSupplyRows] = useState<SupplyRow[]>([
    { id: uid(), startDate: "", endDate: "", mw: "", startTime: "00:00", endTime: "24:00" }
  ]);
  const [returnPct, setReturnPct] = useState("104");
  const [returnQtyMode, setReturnQtyMode] = useState<"pct" | "mwh">("pct");
  const [returnDistMode, setReturnDistMode] = useState<"uniform" | "custom">("uniform");
  const [returnRows, setReturnRows] = useState<ReturnRow[]>([
    { id: uid(), startDate: "", endDate: "", startTime: "00:00", endTime: "24:00", mw: "" }
  ]);
  const [result, setResult] = useState<BankingCalculateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [supplyLabel, setSupplyLabel] = useState("Injection/Export");
  const [lastCalcLabel, setLastCalcLabel] = useState("Injection/Export");
  const [priceBenchmark, setPriceBenchmark] = useState<"auto" | "year" | "2y_avg" | "3y_avg">("auto");
  const [priceYear, setPriceYear] = useState<number>(new Date().getFullYear() - 1);
  const [avgMode, setAvgMode] = useState<"simple" | "wt">("simple");
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const handleClear = () => {
    setSupplyRows([
      { id: uid(), startDate: "", endDate: "", mw: "", startTime: "00:00", endTime: "24:00" }
    ]);
    setReturnPct("104");
    setReturnQtyMode("pct");
    setReturnDistMode("uniform");
    setReturnRows([{ id: uid(), startDate: "", endDate: "", startTime: "00:00", endTime: "24:00", mw: "" }]);
    setResult(null);
    setError(null);
    setPriceBenchmark("auto");
    setPriceYear(new Date().getFullYear() - 1);
    setAvgMode("simple");
  };

  const getSupplyEnergyMwh = (): number => {
    let total = 0;
    for (const r of supplyRows) {
      const mw = parseFloat(r.mw) || 0;
      if (!r.startDate || !r.endDate || !mw) continue;
      const days = (new Date(r.endDate).getTime() - new Date(r.startDate).getTime()) / 86400000 + 1;
      total += mw * parseHours(r.startTime || "00:00", r.endTime || "24:00") * days;
    }
    return total;
  };

  const getReturnTargetMwh = (): number => {
    const val = parseFloat(returnPct) || 0;
    return returnQtyMode === "pct" ? getSupplyEnergyMwh() * (val / 100) : val;
  };

  const getAllocatedMwh = (): number => {
    if (returnDistMode !== "custom") return 0;
    let total = 0;
    for (const r of returnRows) {
      const mw = parseFloat(r.mw) || 0;
      if (!r.startDate || !r.endDate) continue;
      const days = (new Date(r.endDate).getTime() - new Date(r.startDate).getTime()) / 86400000 + 1;
      total += mw * parseHours(r.startTime || "00:00", r.endTime || "24:00") * days;
    }
    return total;
  };

  const getReturnTotalHours = (): number => {
    let total = 0;
    for (const r of returnRows) {
      if (!r.startDate || !r.endDate) continue;
      const days = (new Date(r.endDate).getTime() - new Date(r.startDate).getTime()) / 86400000 + 1;
      if (days <= 0) continue;
      total += parseHours(r.startTime || "00:00", r.endTime || "24:00") * days;
    }
    return total;
  };

  const addSupplyRow = (sourceId?: string) => {
    const src = sourceId ? supplyRows.find((r) => r.id === sourceId) : null;
    setSupplyRows([...supplyRows, { id: uid(), startDate: src?.startDate || "", endDate: src?.endDate || "", mw: src?.mw || "", startTime: src?.startTime || "00:00", endTime: src?.endTime || "24:00" }]);
  };
  const removeSupplyRow = (id: string) => { if (supplyRows.length > 1) setSupplyRows(supplyRows.filter((r) => r.id !== id)); };
  const updateSupply = (id: string, field: keyof SupplyRow, value: string) => { setSupplyRows(supplyRows.map((r) => (r.id === id ? { ...r, [field]: value } : r))); };

  const addReturnRow = () => { setReturnRows([...returnRows, { id: uid(), startDate: "", endDate: "", startTime: "00:00", endTime: "24:00", mw: "" }]); };
  const removeReturnRow = (id: string) => { if (returnRows.length > 1) setReturnRows(returnRows.filter((r) => r.id !== id)); };
  const updateReturn = (id: string, field: keyof ReturnRow, value: string) => { setReturnRows(returnRows.map((r) => (r.id === id ? { ...r, [field]: value } : r))); };

  const handleQtyModeChange = (newMode: "pct" | "mwh") => {
    if (newMode === returnQtyMode) return;
    const supply = getSupplyEnergyMwh();
    const currentVal = parseFloat(returnPct) || 0;
    if (newMode === "mwh") {
      const converted = (currentVal / 100) * supply;
      setReturnPct(converted % 1 === 0 ? converted.toString() : converted.toFixed(2));
    } else {
      const converted = supply > 0 ? (currentVal / supply) * 100 : 104;
      setReturnPct(converted % 1 === 0 ? converted.toString() : converted.toFixed(2));
    }
    setReturnQtyMode(newMode);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const supply_schedule = supplyRows.filter((r) => r.startDate && r.endDate && r.mw).map((r) => ({ start_date: r.startDate, end_date: r.endDate, quantum_mw: parseFloat(r.mw), start_time: r.startTime || "00:00", end_time: r.endTime || "24:00" }));
    const return_schedule = returnRows.filter((r) => r.startDate && r.endDate).map((r) => {
      const entry: any = { start_date: r.startDate, end_date: r.endDate, start_time: r.startTime || "00:00", end_time: r.endTime || "24:00" };
      if (returnDistMode === "custom" && r.mw) entry.quantum_mw = parseFloat(r.mw);
      return entry;
    });
    if (!supply_schedule.length) { setError("Add at least one supply period."); setLoading(false); return; }
    if (!return_schedule.length) { setError("Add at least one return period."); setLoading(false); return; }
    const supplyMwh = getSupplyEnergyMwh();
    const rawVal = parseFloat(returnPct) || 104;
    const pctForApi = returnQtyMode === "pct" ? rawVal : supplyMwh > 0 ? (rawVal / supplyMwh) * 100 : 104;
    try {
      const params: any = {
        supply_schedule,
        return_schedule,
        return_pct: pctForApi,
        return_mode: returnDistMode,
        price_benchmark: priceBenchmark,
        schedule_type: supplyLabel,
      };
      if (priceBenchmark === "year") params.price_year = priceYear;
      if (priceBenchmark === "2y_avg" || priceBenchmark === "3y_avg") params.avg_mode = avgMode;
      const resp = await calculateBankingCost(params);
      setResult(resp);
      setLastCalcLabel(supplyLabel);
    } catch (err: any) { setError(err?.message || "Calculation failed"); }
    setLoading(false);
  };

  const handleExport = async () => {
    if (!result) return;
    setExporting(true);
    try {
      const payload: any = {
        ...result,
        schedule_type: lastCalcLabel as "Injection/Export" | "Drawal/Import",
        price_benchmark: priceBenchmark,
      };
      if (priceBenchmark === "year") payload.price_year = priceYear;
      if (priceBenchmark === "2y_avg" || priceBenchmark === "3y_avg") payload.avg_mode = avgMode;
      const blob = await exportBankingExcel(payload);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const suffix = lastCalcLabel === "Drawal/Import" ? "Drawal" : "Injection";
      a.href = url; a.download = `Banking_Analysis_${suffix}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setError("Export failed"); }
    setExporting(false);
  };

  const target = getReturnTargetMwh();
  const allocated = getAllocatedMwh();
  const barPct = target > 0 ? Math.min((allocated / target) * 100, 100) : 0;
  const trackerMatch = target > 0 && Math.abs(allocated - target) < target * 0.01;
  const trackerOver = allocated > target;

  const renderBadges = (p: BankingPeriodResult) => (
    <div className="flex items-center justify-center gap-1 mt-0.5 w-full">
      {p.price_source_label ? (
        <span className="whitespace-nowrap text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-100 uppercase leading-none">{p.price_source_label}</span>
      ) : (
        <>
          {p.actual_days > 0 && <span className="whitespace-nowrap text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 uppercase leading-none">{p.source_year}</span>}
          {p.forecast_days > 0 && <span className="whitespace-nowrap text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100 uppercase leading-none">{p.source_year}</span>}
        </>
      )}
    </div>
  );



  const inp = "w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all duration-200 bg-white text-gray-700 text-sm";
  const inpSm = "w-full px-2 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all duration-200 bg-white text-gray-700 text-sm";
  const lbl = "block font-medium text-xs mb-1 text-gray-700";

  return (
    <div className="w-full">


      <form onSubmit={handleSubmit} className="bg-white rounded-xl p-5 border border-gray-100">
        {/* Parties Removed */}

        {/* Top Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Injection Card */}
          <div className="relative overflow-hidden rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-4 shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] transition-all hover:shadow-md hover:-translate-y-0.5">
            <div className="absolute -top-4 -right-4 h-16 w-16 rounded-full bg-indigo-100/60 blur-xl" />
            <div className="flex flex-col relative z-10">
              <span className="text-[10px] font-medium uppercase tracking-wider text-indigo-500 mb-1 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" /> Total {supplyLabel}
              </span>
              <div className="flex items-end gap-1.5 mt-1">
                <span className="text-2xl font-medium text-indigo-900 leading-none tracking-tight">{fmtN(getSupplyEnergyMwh())}</span>
                <span className="text-xs font-medium text-indigo-600 mb-0.5">MWh</span>
              </div>
            </div>
          </div>

          {/* Return Required Card */}
          <div className="relative overflow-hidden rounded-xl border border-orange-100 bg-gradient-to-br from-orange-50 to-white p-4 shadow-[0_2px_10px_-3px_rgba(255,154,60,0.1)] transition-all hover:shadow-md hover:-translate-y-0.5">
            <div className="absolute -top-4 -right-4 h-16 w-16 rounded-full bg-orange-100/60 blur-xl" />
            <div className="flex flex-col relative z-10">
              <span className="flex justify-between items-center text-[10px] font-medium uppercase tracking-wider text-orange-600 mb-1">
                Return Required
                <span className="text-[9px] font-normal text-orange-400 capitalize normal-case tracking-normal">(as per Banking Factor)</span>
              </span>
              <div className="flex items-end gap-1.5 mt-1">
                <span className="text-2xl font-medium text-orange-900 leading-none tracking-tight">{fmtN(target)}</span>
                <span className="text-xs font-medium text-orange-600 mb-0.5">MWh</span>
              </div>
            </div>
          </div>

          {/* Return Remaining Card */}
          <div className={`relative overflow-hidden rounded-xl border transition-all hover:shadow-md hover:-translate-y-0.5 p-4 ${returnDistMode === "custom" && target - allocated < 0 ? "border-red-200 bg-gradient-to-br from-red-50 to-white shadow-[0_2px_10px_-3px_rgba(239,68,68,0.15)]" : returnDistMode === "custom" && target - allocated === 0 ? "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white shadow-[0_2px_10px_-3px_rgba(16,185,129,0.15)]" : "border-slate-200 bg-gradient-to-br from-slate-50 to-white shadow-[0_2px_10px_-3px_rgba(100,116,139,0.1)]"}`}>
            <div className={`absolute -top-4 -right-4 h-16 w-16 rounded-full blur-xl ${returnDistMode === "custom" && target - allocated < 0 ? "bg-red-200/50" : returnDistMode === "custom" && target - allocated === 0 ? "bg-emerald-200/50" : "bg-slate-200/50"}`} />
            <div className="flex flex-col relative z-10">
              <span className={`text-[10px] font-medium uppercase tracking-wider mb-1 ${returnDistMode === "custom" && target - allocated < 0 ? "text-red-600" : returnDistMode === "custom" && target - allocated === 0 ? "text-emerald-600" : "text-slate-500"}`}>
                Return Remaining <i className="fas fa-info-circle ml-1 opacity-60" title="Target minus allocated" />
              </span>
              <div className="flex items-end gap-1.5 mt-1">
                <span className={`text-2xl font-medium leading-none tracking-tight ${returnDistMode === "custom" && target - allocated < 0 ? "text-red-700" : returnDistMode === "custom" && target - allocated === 0 ? "text-emerald-700" : "text-slate-700"}`}>
                  {returnDistMode === "custom" ? fmtN(target - allocated) : "0"}
                </span>
                <span className={`text-xs font-medium mb-0.5 ${returnDistMode === "custom" && target - allocated < 0 ? "text-red-600" : returnDistMode === "custom" && target - allocated === 0 ? "text-emerald-600" : "text-slate-500"}`}>MWh</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Price Benchmark ── */}
        <div className="bg-gradient-to-br from-violet-50/80 to-white p-3.5 rounded-xl border border-violet-100 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 mr-1 group relative">
              <Settings2 className="w-3.5 h-3.5 text-violet-500" />
              <span className="text-xs font-medium text-violet-700">Price Benchmark</span>
              <Info className="w-3 h-3 text-violet-400 cursor-help" />

              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-2.5 bg-gray-900 text-white text-[10px] rounded-lg shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-[100] border border-gray-700 leading-relaxed text-center">
                Determines the reference DAM prices used for <span className="text-violet-300 font-medium">calculating cost</span> of banked energy.
                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900" />
              </div>
            </div>
            <div className="flex bg-white rounded-lg p-0.5 gap-0.5 shadow-sm border border-violet-100">
              {(["auto", "year", "2y_avg", "3y_avg"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPriceBenchmark(m)}
                  className={`px-3 py-1 rounded-md text-[11px] font-medium transition-all ${priceBenchmark === m
                    ? "bg-violet-600 text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                >
                  {{ auto: "Auto", year: "Specific Year", "2y_avg": "2Y Average", "3y_avg": "3Y Average" }[m]}
                </button>
              ))}
            </div>
            {priceBenchmark === "year" && (
              <select
                value={priceYear}
                onChange={(e) => setPriceYear(Number(e.target.value))}
                className="ml-1 px-2 py-1 border-2 border-violet-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 bg-white"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            )}
            {(priceBenchmark === "2y_avg" || priceBenchmark === "3y_avg") && (
              <div className="flex bg-white rounded-lg p-0.5 gap-0.5 shadow-sm border border-violet-100 ml-1">
                {(["simple", "wt"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setAvgMode(m)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${avgMode === m
                      ? "bg-violet-100 text-violet-800 shadow-sm"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    {m === "simple" ? "Simple Avg" : "Wt. Avg"}
                  </button>
                ))}
              </div>
            )}
            <span className="text-[10px] text-gray-400 ml-auto italic">
              {priceBenchmark === "auto" && "Uses actual DAM data, falls back to previous years if missing"}
              {priceBenchmark === "year" && `Uses ${priceYear} DAM data`}
              {priceBenchmark === "2y_avg" && "Average of DAM data from 2 most recent complete years"}
              {priceBenchmark === "3y_avg" && "Average of DAM data from 3 most recent complete years"}
            </span>
          </div>
        </div>

        {/* ── Supply + Return side by side ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
          {/* Supply */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between mb-4 px-4 py-2.5 bg-indigo-50/30 border-b border-indigo-50">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md flex items-center justify-center shadow-sm" style={{ background: PrimaryColor }}><ArrowRight className="w-3 h-3 text-white" /></div>
                <div className="flex bg-white rounded-lg p-0.5 shadow-sm border border-indigo-100 items-center">
                  {["Injection/Export", "Drawal/Import"].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setSupplyLabel(m)}
                      className={`px-3 py-1 rounded-md text-[11px] font-medium transition-all ${supplyLabel === m
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                <span className="font-medium text-sm text-gray-400">Schedule</span>
              </div>
              <button type="button" onClick={() => addSupplyRow()} className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-lg transition-all" style={{ color: PrimaryColor, background: "#fff7ed" }}>
                <Plus className="w-3 h-3" />Add
              </button>
            </div>
            <div className="px-4 pb-4 space-y-2">
              {supplyRows.map((row) => (
                <div key={row.id} className="bg-gradient-to-br from-gray-50 to-white p-3 rounded-lg border border-gray-200">
                  <div className="flex flex-row items-end gap-2 flex-wrap">
                    <div className="flex-1 min-w-[250px]">
                      <label className={lbl}>Date Range <span className="text-red-500">*</span></label>
                      <DynamicDatePicker
                        setDate={(dates) => {
                          if (dates && dates.length > 0) {
                            const st = formatLocal(dates[0].startDate);
                            const et = formatLocal(dates[0].endDate);
                            setSupplyRows(prev => prev.map(r => r.id === row.id ? { ...r, startDate: st, endDate: et } : r));
                          }
                        }}
                        defaultDates={row.startDate ? { startDate: new Date(row.startDate), endDate: row.endDate ? new Date(row.endDate) : new Date(row.startDate), key: "selection" } : undefined}
                        buttonClassName={inpSm + " text-left cursor-pointer"}
                      />
                    </div>
                    <div className="w-[70px]">
                      <label className={lbl}>MW <span className="text-red-500">*</span></label>
                      <input type="number" value={row.mw} onChange={(e) => updateSupply(row.id, "mw", e.target.value)} className={inpSm} min="0" step="any" />
                    </div>
                    <div className="flex-1 min-w-[80px]">
                      <label className={lbl}>From Time</label>
                      <select value={row.startTime} onChange={(e) => updateSupply(row.id, "startTime", e.target.value)} className={inpSm}>
                        {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="flex-1 min-w-[80px]">
                      <label className={lbl}>To Time</label>
                      <select value={row.endTime} onChange={(e) => updateSupply(row.id, "endTime", e.target.value)} className={inpSm}>
                        {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="flex gap-1 items-end">
                      <button type="button" onClick={() => addSupplyRow(row.id)} className="w-7 h-7 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center transition-all" title="Duplicate"><Copy className="w-3 h-3" /></button>
                      {supplyRows.length > 1 && <button type="button" onClick={() => removeSupplyRow(row.id)} className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-all" title="Remove"><Trash2 className="w-3 h-3" /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Return */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between mb-4 px-4 py-2.5 bg-emerald-50/30 border-b border-emerald-50">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md flex items-center justify-center shadow-sm" style={{ background: SecondaryColor }}><ArrowLeft className="w-3 h-3 text-white" /></div>
                <span className="font-medium text-sm text-gray-700">Return Schedule</span>
              </div>
              <button type="button" onClick={addReturnRow} className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-lg transition-all" style={{ color: SecondaryColor, background: "#f0fdfa" }}>
                <Plus className="w-3 h-3" />Add
              </button>
            </div>
            <div className="px-4 pb-4 space-y-3">
              {/* Config */}
              <div className="bg-gradient-to-br from-teal-50/50 to-white p-3 rounded-lg border border-teal-100/50 mb-2 space-y-3 shadow-sm">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-1 w-32 shrink-0 group relative cursor-help">
                    <span className="text-xs font-medium text-gray-500 whitespace-nowrap">B.F / Quantum</span>
                    <Info className="w-2.5 h-2.5 text-gray-400" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-gray-900 text-white text-[9px] rounded shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all z-[110] leading-tight normal-case font-normal text-center">
                      Banking Factor (B.F) determines the total energy quantum available for return from the injected units.
                      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900" />
                    </div>
                  </div>
                  <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5 shrink-0">
                    {(["pct", "mwh"] as const).map((m) => (
                      <button key={m} type="button" onClick={() => handleQtyModeChange(m)} className={`px-2.5 py-0.5 rounded-md text-[11px] font-medium transition-all ${returnQtyMode === m ? "bg-white text-gray-800 shadow-sm" : "text-gray-400"}`}>
                        {m === "pct" ? "%" : "MWh"}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="number" value={returnPct} onChange={(e) => setReturnPct(e.target.value)} className="w-20 px-2 py-1 border-2 border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100" step={returnQtyMode === "pct" ? "0.5" : "100"} />
                    <span className="text-xs text-gray-400">{returnQtyMode === "pct" ? "%" : "MWh"}</span>
                    {returnQtyMode === "pct" && <span className="text-[10px] text-gray-400 italic">= {fmtN(target)} MWh</span>}
                    {returnQtyMode === "mwh" && getSupplyEnergyMwh() > 0 && (
                      <span className="text-[10px] text-gray-400 italic">= {((parseFloat(returnPct) || 0) / getSupplyEnergyMwh() * 100).toFixed(2)}%</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs font-medium text-gray-500 w-32 shrink-0 whitespace-nowrap">Distribution</span>
                  <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
                    {(["uniform", "custom"] as const).map((m) => (
                      <button key={m} type="button" onClick={() => setReturnDistMode(m)} className={`px-2.5 py-0.5 rounded-md text-[11px] font-medium transition-all ${returnDistMode === m ? "bg-white text-gray-800 shadow-sm" : "text-gray-400"}`}>
                        {m === "uniform" ? "Uniform" : "Custom MW"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Rows */}
              <div className="space-y-2">
                {returnRows.map((row) => (
                  <div key={row.id} className="bg-gradient-to-br from-gray-50 to-white p-3 rounded-lg border border-gray-200">
                    <div className="flex flex-row items-end gap-2 flex-wrap">
                      <div className="flex-1 min-w-[260px]">
                        <label className={lbl}>Date Range <span className="text-red-500">*</span></label>
                        <DynamicDatePicker
                          setDate={(dates) => {
                            if (dates && dates.length > 0) {
                              const st = formatLocal(dates[0].startDate);
                              const et = formatLocal(dates[0].endDate);
                              setReturnRows(prev => prev.map(r => r.id === row.id ? { ...r, startDate: st, endDate: et } : r));
                            }
                          }}
                          defaultDates={row.startDate ? { startDate: new Date(row.startDate), endDate: row.endDate ? new Date(row.endDate) : new Date(row.startDate), key: "selection" } : undefined}
                          buttonClassName={inpSm + " text-left cursor-pointer"}
                        />
                      </div>
                      <div className="flex-1 min-w-[70px]">
                        <label className={lbl}>From Time</label>
                        <select value={row.startTime} onChange={(e) => updateReturn(row.id, "startTime", e.target.value)} className={inpSm}>
                          {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div className="flex-1 min-w-[70px]">
                        <label className={lbl}>To Time</label>
                        <select value={row.endTime} onChange={(e) => updateReturn(row.id, "endTime", e.target.value)} className={inpSm}>
                          {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      {returnDistMode === "custom" ? (
                        <div className="w-[70px]">
                          <label className={lbl}>MW</label>
                          <input type="number" value={row.mw} onChange={(e) => updateReturn(row.id, "mw", e.target.value)} className={inpSm} min="0" step="any" />
                        </div>
                      ) : (
                        <div className="w-[70px]">
                          <label className="block font-medium text-xs mb-1 text-gray-400 opacity-70">MW</label>
                          <input
                            type="text"
                            disabled
                            value={row.startDate && row.endDate && getReturnTotalHours() > 0 ? (getReturnTargetMwh() / getReturnTotalHours()).toFixed(2) : ""}
                            className="w-full px-2 py-2 border-2 border-gray-200 rounded-lg bg-gray-100 text-gray-400 text-sm cursor-not-allowed opacity-70"
                            title="Uniformly distributed allocation"
                          />
                        </div>
                      )}
                      <div className="flex gap-1 items-end">
                        {returnRows.length > 1 && <button type="button" onClick={() => removeReturnRow(row.id)} className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-all" title="Remove"><Trash2 className="w-3 h-3" /></button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-center gap-3 pt-3">
          <button type="button" onClick={handleClear} className="px-6 py-2 rounded-lg text-gray-700 font-medium text-sm border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 transform hover:scale-105">
            <i className="fas fa-redo mr-1.5 text-xs" />Clear
          </button>
          <button type="submit" disabled={loading} className="px-6 py-2 rounded-lg text-white font-medium text-sm transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: loading ? "#ccc" : `linear-gradient(135deg, ${PrimaryColor}, #ff9a3c)` }}>
            {loading ? <><i className="fas fa-spinner fa-spin mr-1.5 text-xs" />Processing...</> : <><Zap className="w-4 h-4 inline mr-1.5" />Calculate</>}
          </button>
        </div>

      </form>

      {error && (
        <div className="mt-3 bg-red-50 border-l-4 border-red-500 p-3 rounded-lg">
          <div className="flex items-center"><i className="fas fa-exclamation-circle text-red-500 mr-2 text-sm" /><p className="text-red-700 font-medium text-sm">{error}</p></div>
        </div>
      )}

      {/* ── Results ── */}
      {result && (
        <div className="mt-3 bg-white rounded-xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
            <h2 className="text-base font-medium text-gray-800">Banking Analysis</h2>
            <button type="button" onClick={handleExport} disabled={exporting} className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium border border-green-200 text-green-700 hover:bg-green-50 transition-all disabled:opacity-50">
              <Download className="w-4 h-4" />{exporting ? "Exporting..." : "Export Excel"}
            </button>
          </div>

          {/* Summary Cards */}
          <div className="flex flex-row gap-3 mb-4">
            <div className="flex flex-col items-center bg-gradient-to-br from-white to-indigo-50 p-4 rounded-lg hover:scale-105 transition-all duration-300 flex-1 border border-indigo-100">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center mb-2"><ArrowRight className="w-5 h-5 text-white" /></div>
              <h3 className="text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">{lastCalcLabel === "Injection/Export" ? "Injection/Export Cost" : "Drawal/Import Value"}</h3>
              <p className="text-xl font-medium text-gray-800 mb-0.5">{formatCr(result.supply.total_cost)}</p>
              <div className="flex gap-1.5 items-center">
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">{fmtN(result.supply.total_energy_mwh)} MWh</span>
                <span className="text-[10px] font-bold text-indigo-500">Rs {getPU(result.supply.total_cost, result.supply.total_energy_mwh)}/kWh</span>
              </div>
            </div>
            <div className="flex flex-col items-center bg-gradient-to-br from-white to-emerald-50 p-4 rounded-lg hover:scale-105 transition-all duration-300 flex-1 border border-emerald-100">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mb-2"><ArrowLeft className="w-5 h-5 text-white" /></div>
              <h3 className="text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">{lastCalcLabel === "Injection/Export" ? "Return Value" : "Return Cost"}</h3>
              <p className="text-xl font-medium text-gray-800 mb-0.5">{formatCr(result.return.total_cost)}</p>
              <div className="flex gap-1.5 items-center">
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">{fmtN(result.return.total_energy_mwh)} MWh</span>
                <span className="text-[10px] font-bold text-emerald-600">Rs {getPU(result.return.total_cost, result.return.total_energy_mwh)}/kWh</span>
              </div>
            </div>
            {(() => {
              const isZero = Math.abs(result.net_savings) < 1000;
              const isProfitable = result.verdict === "PROFITABLE";

              const borderClass = isZero ? "border-gray-200" : (isProfitable ? "border-emerald-200" : "border-red-200");
              const bgGradient = isZero ? "from-white to-gray-50" : (isProfitable ? "from-white to-emerald-50" : "from-white to-red-50");
              const iconGradient = isZero ? "from-gray-400 to-gray-600" : (isProfitable ? "from-emerald-400 to-emerald-600" : "from-red-400 to-red-600");
              const textClass = isZero ? "text-gray-600" : (isProfitable ? "text-emerald-600" : "text-red-600");
              const puTextClass = isZero ? "text-gray-400" : (isProfitable ? "text-emerald-600" : "text-red-500");

              return (
                <div className={`flex flex-col items-center bg-gradient-to-br ${bgGradient} p-4 rounded-lg hover:scale-105 transition-all duration-300 flex-1 border-2 ${borderClass}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 bg-gradient-to-br ${iconGradient}`}>
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Net Savings</h3>
                  <p className={`text-xl font-medium mb-0.5 ${textClass}`}>
                    {formatCr(Math.abs(result.net_savings))}
                  </p>
                  <div className="flex flex-col items-center gap-1">
                    <span className={`text-[10px] font-bold ${puTextClass}`}>
                      Rs {getPU(Math.abs(result.net_savings), result.supply.total_energy_mwh)}/kWh
                    </span>
                    {!isZero && (
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${isProfitable ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {result.verdict}
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ── Tables side by side ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {/* Supply table */}
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 bg-indigo-50 border-b border-indigo-100 flex items-center gap-2">
                <ArrowRight className="w-3 h-3 text-indigo-500" />
                <span className="text-xs font-medium text-gray-700">{lastCalcLabel === "Injection/Export" ? "Supply Breakdown" : "Drawal Value Breakdown"}</span>
              </div>
              {/* Header */}
              <div className="grid grid-cols-12 bg-gray-50 border-b border-gray-100">
                <div className="col-span-3"><TH>Period</TH></div>
                <div className="col-span-2"><TH align="center">Time</TH></div>
                <div className="col-span-1"><TH align="center">MW</TH></div>
                <div className="col-span-2"><TH align="center">MWh</TH></div>
                <div className="col-span-2">
                  <TH align="center">
                    <div className="flex items-center justify-center gap-1 group relative">
                      <span>Rs/kWh</span>
                      <Info className="w-2.5 h-2.5 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-24 p-1.5 bg-gray-900 text-white text-[9px] rounded shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all z-[110] leading-tight normal-case font-normal text-center">
                        DAM Price
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900" />
                      </div>
                    </div>
                  </TH>
                </div>
                <div className="col-span-2"><TH align="center">Cost</TH></div>
              </div>
              {/* Rows */}
              {result.supply.periods.map((p, i) => (
                <div key={i} className={`grid grid-cols-12 border-b border-gray-50 hover:bg-gray-50/50 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/30"}`}>
                  <div className="col-span-3"><TD><span className="text-gray-700">{p.period}</span></TD></div>
                  <div className="col-span-2"><TD align="center">{p.time_range}</TD></div>
                  <div className="col-span-1"><TD align="center">{fmtN(p.quantum_mw!)}</TD></div>
                  <div className="col-span-2"><TD align="center">{fmtN(p.energy_mwh)}</TD></div>
                  <div className="col-span-2"><TD align="center"><div className="flex flex-col items-center"><span className="text-[10px] text-gray-400 font-medium leading-none">Rs {getPU(p.cost, p.energy_mwh)}</span>{renderBadges(p)}</div></TD></div>
                  <div className="col-span-2"><TD align="center"><span className="text-indigo-600 font-medium">{formatCr(p.cost)}</span></TD></div>
                </div>
              ))}
              {/* Total */}
              <div className="grid grid-cols-12 bg-indigo-50/60">
                <div className="col-span-3"><TD><span className="text-gray-700 font-semibold">Total</span></TD></div>
                <div className="col-span-2"><TD align="center">-</TD></div>
                <div className="col-span-1"><TD align="center">-</TD></div>
                <div className="col-span-2"><TD align="center">{fmtN(result.supply.total_energy_mwh)}</TD></div>
                <div className="col-span-2"><TD align="center"><span className="text-[10px] text-indigo-700 font-bold">{getPU(result.supply.total_cost, result.supply.total_energy_mwh)}</span></TD></div>
                <div className="col-span-2"><TD align="center"><span className="text-indigo-700 font-bold">{formatCr(result.supply.total_cost)}</span></TD></div>
              </div>
            </div>

            {/* Return table */}
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
                <ArrowLeft className="w-3 h-3 text-emerald-500" />
                <span className="text-xs font-medium text-gray-700">{lastCalcLabel === "Injection/Export" ? "Return Breakdown" : "Return Cost Breakdown"}</span>
                <span className="text-[10px] text-gray-400 ml-auto">{result.return.return_pct}% | {result.return.return_mode}</span>
              </div>
              {/* Header */}
              <div className="grid grid-cols-12 bg-gray-50 border-b border-gray-100">
                <div className="col-span-3"><TH>Period</TH></div>
                <div className="col-span-2"><TH align="center">Time</TH></div>
                <div className="col-span-1"><TH align="center">MW</TH></div>
                <div className="col-span-2"><TH align="center">MWh</TH></div>
                <div className="col-span-2">
                  <TH align="center">
                    <div className="flex items-center justify-center gap-1 group relative">
                      <span>Rs/kWh</span>
                      <Info className="w-2.5 h-2.5 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-gray-900 text-white text-[9px] rounded shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all z-[110] leading-tight normal-case font-normal text-center">
                        IEX-DAM Price for this specific date range and time slot.
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900" />
                      </div>
                    </div>
                  </TH>
                </div>
                <div className="col-span-2"><TH align="center">Value</TH></div>
              </div>
              {/* Rows */}
              {result.return.periods.map((p, i) => (
                <div key={i} className={`grid grid-cols-12 border-b border-gray-50 hover:bg-gray-50/50 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/30"}`}>
                  <div className="col-span-3"><TD><span className="text-gray-700">{p.period}</span></TD></div>
                  <div className="col-span-2"><TD align="center">{p.time_range}</TD></div>
                  <div className="col-span-1"><TD align="center">{fmtN(p.return_mw!)}</TD></div>
                  <div className="col-span-2"><TD align="center">{fmtN(p.energy_mwh)}</TD></div>
                  <div className="col-span-2"><TD align="center"><div className="flex flex-col items-center"><span className="text-[10px] text-gray-400 font-medium leading-none">Rs {getPU(p.cost, p.energy_mwh)}</span>{renderBadges(p)}</div></TD></div>
                  <div className="col-span-2"><TD align="center"><span className="text-emerald-600 font-medium">{formatCr(p.cost)}</span></TD></div>
                </div>
              ))}
              {/* Total */}
              <div className="grid grid-cols-12 bg-emerald-50/60">
                <div className="col-span-3"><TD><span className="text-gray-700 font-semibold">Total</span></TD></div>
                <div className="col-span-2"><TD align="center">-</TD></div>
                <div className="col-span-1"><TD align="center">-</TD></div>
                <div className="col-span-2"><TD align="center">{fmtN(result.return.total_energy_mwh)}</TD></div>
                <div className="col-span-2"><TD align="center"><span className="text-[10px] text-emerald-700 font-bold">{getPU(result.return.total_cost, result.return.total_energy_mwh)}</span></TD></div>
                <div className="col-span-2"><TD align="center"><span className="text-emerald-700 font-bold">{formatCr(result.return.total_cost)}</span></TD></div>
              </div>
            </div>
          </div>

          {/* ── Breakeven Analysis ── */}
          {result.breakeven?.mode && (
            <div className="mt-4 bg-gradient-to-r from-amber-50 via-white to-amber-50 rounded-xl border border-amber-200 p-3.5 flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0">
                  <Scale className="w-4 h-4 text-white" />
                </div>
                <div>
                  <span className="text-[10px] font-medium uppercase tracking-wider text-amber-600 block">Breakeven Point</span>
                  <span className="text-[10px] text-gray-400">Return at which Net Savings = ₹0</span>
                </div>
              </div>

              {result.breakeven.mode === "uniform" && result.breakeven.pct != null && (
                <div className="flex items-center gap-4 ml-auto">
                  <div className="text-center">
                    <span className="text-lg font-bold text-amber-700">{result.breakeven.pct.toFixed(2)}%</span>
                    <span className="text-[10px] text-gray-400 block">Return Factor</span>
                  </div>
                  <div className="w-px h-8 bg-amber-200" />
                  <div className="text-center">
                    <span className="text-lg font-bold text-amber-700">{fmtN(result.breakeven.mwh!)}</span>
                    <span className="text-[10px] text-gray-400 block">MWh</span>
                  </div>
                  <div className="w-px h-8 bg-amber-200" />
                  <div className="text-center">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${Math.abs(result.return.return_pct - result.breakeven.pct) < 0.01
                      ? "bg-gray-100 text-gray-700"
                      : lastCalcLabel === "Injection/Export"
                        ? (result.return.return_pct > result.breakeven.pct ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700")
                        : (result.return.return_pct < result.breakeven.pct ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700")
                      }`}>
                      {Math.abs(result.return.return_pct - result.breakeven.pct) < 0.01
                        ? "At breakeven"
                        : result.return.return_pct > result.breakeven.pct
                          ? `+${(result.return.return_pct - result.breakeven.pct).toFixed(2)}% above`
                          : `${(result.breakeven.pct - result.return.return_pct).toFixed(2)}% below`}
                    </span>
                    <span className="text-[10px] text-gray-400 block mt-0.5">vs Current ({result.return.return_pct}%)</span>
                  </div>
                  <div className="w-px h-8 bg-amber-200" />
                  <button
                    type="button"
                    onClick={() => {
                      if (returnQtyMode === "mwh") {
                        setReturnPct(result.breakeven.mwh!.toFixed(2));
                      } else {
                        setReturnPct(result.breakeven.pct!.toFixed(2));
                      }
                    }}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white transition-all shadow-sm hover:shadow-md"
                  >
                    <Zap className="w-3 h-3 inline mr-1" />Apply Breakeven
                  </button>
                </div>
              )}

              {result.breakeven.mode === "custom" && result.breakeven.scale_factor != null && (
                <div className="flex items-center gap-4 ml-auto">
                  <div className="text-center">
                    <span className="text-lg font-bold text-amber-700">{result.breakeven.scale_factor.toFixed(4)}x</span>
                    <span className="text-[10px] text-gray-400 block">Scale Factor</span>
                  </div>
                  <div className="w-px h-8 bg-amber-200" />
                  <div className="text-center">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${Math.abs(result.breakeven.scale_factor - 1) < 0.0001
                      ? "bg-gray-100 text-gray-700"
                      : lastCalcLabel === "Injection/Export"
                        ? (result.breakeven.scale_factor < 1 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")
                        : (result.breakeven.scale_factor > 1 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700")
                      }`}>
                      {Math.abs(result.breakeven.scale_factor - 1) < 0.0001
                        ? "Already at breakeven"
                        : lastCalcLabel === "Injection/Export"
                          ? (result.breakeven.scale_factor > 1
                            ? `MW needs to increase by ${((result.breakeven.scale_factor - 1) * 100).toFixed(1)}%`
                            : `MW can reduce by ${((1 - result.breakeven.scale_factor) * 100).toFixed(1)}%`)
                          : (result.breakeven.scale_factor > 1
                            ? `MW can increase by ${((result.breakeven.scale_factor - 1) * 100).toFixed(1)}%`
                            : `MW needs to reduce by ${((1 - result.breakeven.scale_factor) * 100).toFixed(1)}%`)}
                    </span>
                  </div>
                  <div className="w-px h-8 bg-amber-200" />
                  <button
                    type="button"
                    onClick={() => {
                      if (result.breakeven.period_mws) {
                        setReturnRows(prev => prev.map((r, i) => ({
                          ...r,
                          mw: result.breakeven.period_mws![i] != null ? result.breakeven.period_mws![i].toString() : r.mw,
                        })));
                        const newAllocated = result.return.total_energy_mwh * result.breakeven.scale_factor!;
                        if (returnQtyMode === "mwh") {
                          setReturnPct(newAllocated.toFixed(2));
                        } else {
                          const pct = result.supply.total_energy_mwh > 0 ? (newAllocated / result.supply.total_energy_mwh) * 100 : 0;
                          setReturnPct(pct.toFixed(2));
                        }
                      }
                    }}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white transition-all shadow-sm hover:shadow-md"
                  >
                    <Zap className="w-3 h-3 inline mr-1" />Apply Breakeven MWs
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BankingCalculatorForm;
